// Fines on a driver's page and on an owner's row. The same rows the Traffic
// Fines page shows for that person, from the same function behind the same
// gate, and nothing more: someone without can_manage_fines sees no panel at all.
//
// On a driver's page a proposal is not a fine. A fine where a manager confirmed
// this driver was driving is listed apart from one where the roster or the old sheet only
// proposes the name, and says so, because the difference is the whole point.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, Ticket } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../lib/shadcn/dialog'
import { Spinner } from '../../lib/shadcn/spinner'
import { Chip } from '../interviews/Chip'
import { useGetEvidenceUrl, useGetFinesAccess, useGetFinesQueue } from '../../hooks/backend/fines'
import { formatZar, methodLabel, sourceLabel, statusLabel, statusTone, whenLabel, type QueueResult, type QueueRow } from '../../utils/fines'

type PersonQueue = QueueResult & { driver_id?: string | null; owner_id?: string | null }

function Rows({ rows, empty }: { rows: QueueRow[]; empty: string }) {
  const evidence = useGetEvidenceUrl<{ url: string }>()
  const [error, setError] = useState<string | null>(null)
  const open = async (id: string) => {
    setError(null)
    try {
      const r = await evidence.trigger({ id }, { skipCache: true })
      if (r?.url) window.open(r.url, '_blank', 'noopener')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">{empty}</p>
  return (
    <>
      {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
      <ul className="divide-y divide-border rounded-md border border-border">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
            <Link to={`/traffic-fines?f=${r.id}`} className="font-mono font-medium text-foreground hover:underline">{r.registration ?? 'No vehicle'}</Link>
            <span className="text-xs text-muted-foreground">{whenLabel(r)}</span>
            <Chip tone={statusTone(r.status)}>{statusLabel(r.status)}</Chip>
            {r.is_history && <Chip tone="neutral">{sourceLabel(r.source)}</Chip>}
            {!r.driver_id && r.top_method && <span className="text-xs text-muted-foreground">{methodLabel(r.top_method)}</span>}
            <span className="ml-auto tabular-nums">{formatZar(r.amount_zar)}</span>
            {r.has_evidence && (
              <Button size="sm" variant="ghost" className="h-7 px-2" disabled={evidence.loading} onClick={() => void open(r.id)}>
                <ExternalLink className="mr-1 h-3.5 w-3.5" /> Notice
              </Button>
            )}
          </li>
        ))}
      </ul>
    </>
  )
}

function useAllowed(): boolean | null {
  const access = useGetFinesAccess<{ allowed: boolean }>()
  const load = access.trigger
  useEffect(() => { void load({}).catch(() => undefined) }, [load])
  if (access.error) return false
  return access.data ? access.data.allowed === true : null
}

export function DriverFinesPanel({ applicationId }: { applicationId: string }) {
  const allowed = useAllowed()
  const queue = useGetFinesQueue<PersonQueue>()
  const load = queue.trigger
  useEffect(() => {
    if (allowed) void load({ driver_application_id: applicationId }, { skipCache: true }).catch(() => undefined)
  }, [allowed, applicationId, load])
  if (!allowed) return null

  const rows = queue.data?.rows ?? []
  const me = queue.data?.driver_id ?? null
  const confirmed = rows.filter((r) => me && r.driver_id === me)
  const proposed = rows.filter((r) => !(me && r.driver_id === me))
  const confirmedTotal = confirmed.reduce((s, r) => s + (r.amount_zar ?? 0), 0)

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <Ticket className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Traffic fines</h3>
        {queue.loading && <Spinner className="h-3.5 w-3.5" />}
        {confirmed.length > 0 && <span className="ml-auto text-xs text-muted-foreground">{confirmed.length} confirmed, {formatZar(confirmedTotal)}</span>}
      </div>
      {queue.error && <p className="text-xs text-rose-700 dark:text-rose-300">{queue.error}</p>}
      <div className="space-y-1">
        <p className="text-xs font-medium text-foreground">A manager confirmed this driver was driving</p>
        <Rows rows={confirmed} empty="None." />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-foreground">Proposed only, nobody has confirmed</p>
        <p className="text-[11px] text-muted-foreground">
          The roster or the old fines sheet puts this driver near these notices. That is a question for a manager, not a finding against the driver.
        </p>
        <Rows rows={proposed} empty="None." />
      </div>
    </div>
  )
}

export function OwnerFinesDialog({ personId, name }: { personId: string | null; name: string }) {
  const allowed = useAllowed()
  const [open, setOpen] = useState(false)
  const queue = useGetFinesQueue<PersonQueue>()
  const load = queue.trigger
  useEffect(() => {
    if (open && allowed && personId) void load({ owner_person_id: personId }, { skipCache: true }).catch(() => undefined)
  }, [open, allowed, personId, load])
  if (!allowed || !personId) return null

  const rows = queue.data?.rows ?? []
  const undecided = rows.filter((r) => !r.is_history && !r.charged_to && !['paid', 'written_off'].includes(r.status)).length

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs">
          <Ticket className="mr-1 h-3.5 w-3.5" /> Fines
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Traffic fines: {name || 'Owner'}</DialogTitle>
          <DialogDescription>
            Notices on this owner's vehicles, with the owner on the statement for the month where there is one.
            {undecided > 0 ? ` ${undecided} still need a decision on who carries them.` : ''}
          </DialogDescription>
        </DialogHeader>
        {queue.loading && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading…</p>}
        {queue.error && <p className="text-sm text-rose-700 dark:text-rose-300">{queue.error}</p>}
        {queue.data && <Rows rows={rows} empty="No fines on this owner's vehicles." />}
      </DialogContent>
    </Dialog>
  )
}
